import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle, Share2, Copy, BedDouble, User, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CountryCombo, ComuneCombo, DobSelect } from "@/components/forms/OccupantFields";
import api from "@/lib/api";
import { errMsg } from "@/lib/errors";

/**
 * Public "complete anagrafica" page.
 *
 * Reads `?code=...` from the URL (booking code OR group_code) and renders a
 * per-room composition form so the holder (and any room contact they share
 * the link with) can fill in the Alloggiati-Web mandatory fields.
 *
 * If `&room=<code>` is also present, only THAT room is shown — used to give
 * each group member a focused link.
 */
const EMPTY = { first_name: "", last_name: "", age: "", gender: "", date_of_birth: "", citizenship: "", birth_country: "", birth_place: "", birth_place_istat: "", birth_province: "", document_type: "", document_number: "", document_issue_place: "" };

function deriveAgeFromDob(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 && age <= 120 ? String(age) : "";
}

function missingFieldsLabel(o) {
  /* Return a friendly Italian list of the first 1-2 fields still
     missing on an occupant, used in the toast feedback so the host /
     guest sees EXACTLY what to fill in instead of a generic "mancano
     dei dati". Order mirrors the visual order on screen for fast
     scanning. */
  const m = [];
  if (!o.first_name?.trim()) m.push("Nome");
  if (!o.last_name?.trim()) m.push("Cognome");
  if (!o.gender) m.push("Sesso");
  if (!o.date_of_birth) m.push("Data di nascita");
  if (!o.citizenship?.trim()) m.push("Cittadinanza");
  if (!o.birth_country?.trim()) m.push("Stato di nascita");
  if (o.birth_country && o.birth_country.toUpperCase() === "ITA" && !o.birth_place?.trim()) {
    m.push("Comune di nascita");
  }
  return m;
}

function alloggiatiReady(o) {
  return missingFieldsLabel(o).length === 0;
}

export default function CheckinCompletePage() {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").trim();
  const onlyRoomCode = (params.get("room") || "").trim();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState({});
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!code) {
      setError("Manca il codice prenotazione nell'URL");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/public/bookings/${encodeURIComponent(code)}/composition`);
        // res.data is either { ...single } OR { group_code, rooms:[...] }
        const norm = res.data?.rooms ? res.data : { rooms: [res.data], group_code: null };
        // Filter by ?room=
        if (onlyRoomCode) {
          norm.rooms = norm.rooms.filter((r) => r.code === onlyRoomCode);
        }
        // Ensure every room has occupants array with default rows (=guests)
        norm.rooms = norm.rooms.map((r) => {
          const desired = Math.max(1, Math.min(r.capacity || 4, r.guests || 1));
          const occ = Array.isArray(r.occupants) && r.occupants.length > 0
            ? r.occupants.map((o) => ({ ...EMPTY, ...o }))
            : Array.from({ length: desired }, () => ({ ...EMPTY }));
          return { ...r, occupants: occ };
        });
        setData(norm);
        // Expand the first room by default
        setExpanded(norm.rooms.length === 1 ? { [norm.rooms[0].code]: true } : { [norm.rooms[0]?.code || ""]: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Prenotazione non trovata");
      } finally {
        setLoading(false);
      }
    })();
  }, [code, onlyRoomCode]);

  const updateOcc = (roomCode, idx, field, value) => {
    setData((prev) => ({
      ...prev,
      rooms: prev.rooms.map((r) => {
        if (r.code !== roomCode) return r;
        const occ = r.occupants.map((o, i) => {
          if (i !== idx) return o;
          const next = { ...o, [field]: value };
          // Auto-derive age from DOB
          if (field === "date_of_birth") next.age = deriveAgeFromDob(value);
          return next;
        });
        return { ...r, occupants: occ };
      }),
    }));
  };

  const addOccupant = (roomCode) => {
    setData((prev) => ({
      ...prev,
      rooms: prev.rooms.map((r) =>
        r.code === roomCode ? { ...r, occupants: [...r.occupants, { ...EMPTY }] } : r
      ),
    }));
  };

  const removeOccupant = (roomCode, idx) => {
    setData((prev) => ({
      ...prev,
      rooms: prev.rooms.map((r) =>
        r.code === roomCode ? { ...r, occupants: r.occupants.filter((_, i) => i !== idx) } : r
      ),
    }));
  };

  const saveRoom = async (room) => {
    setSaving((s) => ({ ...s, [room.code]: true }));
    try {
      const payload = {
        occupants: room.occupants.map((o, i) => ({
          first_name: (o.first_name || "").trim(),
          last_name: (o.last_name || "").trim(),
          age: o.age === "" || o.age == null ? null : Number(o.age),
          gender: o.gender || null,
          date_of_birth: o.date_of_birth || null,
          citizenship: (o.citizenship || "").toUpperCase().trim() || null,
          birth_country: (o.birth_country || "").toUpperCase().trim() || null,
          birth_place: (o.birth_place || "").trim() || null,
          birth_place_istat: (o.birth_place_istat || "").trim() || null,
          birth_province: (o.birth_province || "").toUpperCase().trim() || null,
          // Documento d'identità (iter 135) — pre-compilato qui per velocizzare
          // il check-in con l'app Emmanuel Hospitality all'arrivo.
          document_type: (o.document_type || "").trim() || null,
          document_number: (o.document_number || "").trim().toUpperCase() || null,
          document_issue_place: (o.document_issue_place || "").trim() || null,
          is_primary: i === 0,
        })),
      };
      const res = await api.patch(`/public/bookings/${encodeURIComponent(room.code)}/composition`, payload);
      if (res.data?.occupants_alloggiati_ready) {
        toast.success(`Camera ${room.room_name?.it || room.room_name || room.code}: dati completi ✅`);
      } else {
        // Build a precise, friendly summary so the user knows EXACTLY
        // which guest + which field is still missing — "mancano dei
        // dati" was too vague (host repeatedly typed everything but
        // missed the "Stato di nascita" combo for Guest 1).
        const issues = [];
        room.occupants.forEach((o, i) => {
          const miss = missingFieldsLabel(o);
          if (miss.length > 0) {
            const who = (o.first_name?.trim() || o.last_name?.trim())
              ? `${o.first_name?.trim() || ""} ${o.last_name?.trim() || ""}`.trim()
              : `Ospite ${i + 1}`;
            issues.push(`${who}: ${miss.slice(0, 3).join(", ")}${miss.length > 3 ? "…" : ""}`);
          }
        });
        const detail = issues.length > 0 ? ` · ${issues.join(" · ")}` : "";
        toast.warning(`Mancano dati per la Polizia${detail}`, { duration: 8000 });
        // Auto-scroll to the first incomplete occupant so the host
        // doesn't have to hunt for the red border on a long form.
        try {
          const firstBadIdx = room.occupants.findIndex((o) => missingFieldsLabel(o).length > 0);
          if (firstBadIdx >= 0) {
            const el = document.querySelector(`[data-testid="occ-row-${firstBadIdx}"]`)
              || document.querySelector(`[data-testid^="occ-dob-${firstBadIdx}"]`);
            if (el && typeof el.scrollIntoView === "function") {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }
        } catch (_) { /* best-effort UX nicety */ }
      }
    } catch (e) {
      toast.error(errMsg(e, "Errore durante il salvataggio"));
    } finally {
      setSaving((s) => ({ ...s, [room.code]: false }));
    }
  };

  const shareRoomLink = async (roomCode, roomName) => {
    const baseUrl = window.location.origin + window.location.pathname;
    const url = `${baseUrl}?code=${encodeURIComponent(code)}&room=${encodeURIComponent(roomCode)}`;
    const text = `Per completare il check-in del B&B Emmanuel per la nostra ${roomName}, apri questo link e inserisci i tuoi dati anagrafici: ${url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Check-in B&B Emmanuel", text, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const copyRoomLink = async (roomCode) => {
    const baseUrl = window.location.origin + window.location.pathname;
    const url = `${baseUrl}?code=${encodeURIComponent(code)}&room=${encodeURIComponent(roomCode)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  const allRoomsReady = useMemo(() => {
    if (!data) return false;
    return data.rooms.every((r) => r.occupants.length > 0 && r.occupants.every(alloggiatiReady));
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center" data-testid="checkin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#AC5D45]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6" data-testid="checkin-error">
        <div className="bg-white rounded-xl border border-rose-200 p-6 max-w-md w-full text-center">
          <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-[#1E3A5F] mb-2">Errore</h2>
          <p className="text-sm text-[#635F59]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="font-display text-3xl text-[#1E3A5F] mb-1">Completa anagrafica</h1>
          <p className="text-sm text-[#635F59]">
            Codice: <span className="font-mono text-[#AC5D45]">{data.group_code || data.code || code}</span>
            {data.rooms.length > 1 && ` · ${data.rooms.length} camere`}
          </p>
        </div>

        {/* Alloggiati Web disclaimer */}
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6 flex items-start gap-2 text-xs text-amber-900" data-testid="alloggiati-banner">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Obbligo di legge — Polizia di Stato (Alloggiati Web)</strong>
            <p className="mt-1">Entro 24 ore dall'arrivo dobbiamo trasmettere alla Questura i dati anagrafici di TUTTI gli ospiti, anche minori e familiari. Compila ora per evitare ritardi al check-in.</p>
          </div>
        </div>

        {allRoomsReady && (
          <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-4 mb-6 flex items-start gap-2 text-sm text-emerald-900" data-testid="all-ready-banner">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Tutto pronto!</strong>
              <p className="mt-0.5 text-xs">Tutti gli ospiti hanno i dati completi. Non devi fare altro: ci vediamo all'arrivo 🌞</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {data.rooms.map((room) => {
            const isOpen = !!expanded[room.code];
            const readyCount = room.occupants.filter(alloggiatiReady).length;
            const allRoomReady = room.occupants.length > 0 && readyCount === room.occupants.length;
            const roomLabel = room.room_name?.it || room.room_name || room.code;
            const unitSuffix = room.unit_index && room.unit_index > 1 ? ` #${room.unit_index}` : "";

            return (
              <div key={room.code} className="bg-white rounded-xl border border-[#E8E2D9] overflow-hidden" data-testid={`room-block-${room.code}`}>
                <button
                  type="button"
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#FDFBF7] transition"
                  onClick={() => setExpanded((e) => ({ ...e, [room.code]: !isOpen }))}
                  data-testid={`room-toggle-${room.code}`}
                >
                  <BedDouble className="w-5 h-5 text-[#AC5D45]" />
                  <div className="flex-1">
                    <div className="font-semibold text-[#1E3A5F]">{roomLabel}{unitSuffix}</div>
                    <div className="text-xs text-[#635F59]">
                      {room.check_in} → {room.check_out} · {room.occupants.length} occ.{" "}
                      {allRoomReady ? (
                        <span className="text-emerald-700 font-semibold">· ✓ Completa</span>
                      ) : (
                        <span className="text-amber-700">· {readyCount}/{room.occupants.length} pronti</span>
                      )}
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="w-5 h-5 text-[#9C9890]" /> : <ChevronDown className="w-5 h-5 text-[#9C9890]" />}
                </button>

                {isOpen && (
                  <div className="border-t border-[#E8E2D9] p-4 space-y-3">
                    {data.rooms.length > 1 && !onlyRoomCode && (
                      <div className="flex gap-2 flex-wrap mb-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => shareRoomLink(room.code, roomLabel)}
                          className="border-[#AC5D45] text-[#AC5D45] hover:bg-[#AC5D45]/10 rounded-full h-8 text-xs"
                          data-testid={`share-room-${room.code}`}
                        >
                          <Share2 className="w-3 h-3 mr-1" /> Condividi link per questa camera
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyRoomLink(room.code)}
                          className="border-[#E8E2D9] text-[#635F59] hover:bg-[#FDFBF7] rounded-full h-8 text-xs"
                          data-testid={`copy-room-${room.code}`}
                        >
                          <Copy className="w-3 h-3 mr-1" /> Copia
                        </Button>
                      </div>
                    )}

                    {room.occupants.map((occ, idx) => (
                      <OccupantForm
                        key={idx}
                        occ={occ}
                        idx={idx}
                        onChange={(field, value) => updateOcc(room.code, idx, field, value)}
                        onRemove={() => removeOccupant(room.code, idx)}
                        canRemove={room.occupants.length > 1}
                      />
                    ))}

                    <div className="flex flex-wrap gap-2 justify-between pt-2 border-t border-[#E8E2D9]">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addOccupant(room.code)}
                        className="border-[#E8E2D9] text-[#635F59] rounded-full"
                        disabled={room.occupants.length >= (room.capacity || 4) + 2}
                        data-testid={`add-occupant-${room.code}`}
                      >
                        + Aggiungi ospite
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => saveRoom(room)}
                        disabled={saving[room.code]}
                        className="bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full px-6"
                        data-testid={`save-room-${room.code}`}
                      >
                        {saving[room.code] ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva camera"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-[#9C9890] mt-8">
          Il link rimane attivo fino al check-in. Puoi tornare in qualsiasi momento.
        </p>
      </div>
    </div>
  );
}

function OccupantForm({ occ, idx, onChange, onRemove, canRemove }) {
  const ready = alloggiatiReady(occ);
  const missing = missingFieldsLabel(occ);
  // Helper to apply a soft amber highlight to fields that need attention
  // — kicks in only when something IS missing on the row, so the form
  // stays calm while the user is still typing.
  const needs = (label) => !ready && missing.includes(label);
  const flagCls = (label, extra = "") =>
    `h-10 text-sm ${extra} ${needs(label) ? "border-amber-500 ring-1 ring-amber-300" : ""}`.trim();
  return (
    <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3" data-testid={`occ-row-${idx}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-[#AC5D45]" />
          <span className="font-semibold text-sm text-[#1E3A5F]">
            Ospite {idx + 1}
            {idx === 0 && <span className="ml-1.5 text-[10px] bg-[#AC5D45] text-white px-1.5 py-0.5 rounded-full">CONTATTO CAMERA</span>}
          </span>
          {ready && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[#9C9890] hover:text-rose-600 text-xs"
            data-testid={`remove-occupant-${idx}`}
          >
            Rimuovi
          </button>
        )}
      </div>
      {/* Helpful inline summary of what is still missing on this row,
          so the host doesn't have to scan a multi-section form to find
          the empty combo (typically "Stato di nascita"). */}
      {!ready && missing.length > 0 && (
        <div className="mb-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid={`occ-missing-${idx}`}>
          ⚠️ Da completare: <strong>{missing.join(" · ")}</strong>
        </div>
      )}

      <div className="space-y-2.5">
        {/* Nome + Cognome — pieni perché contengono testo lungo */}
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[140px]">
            <Label className="text-[11px] text-[#635F59]">Nome *</Label>
            <Input value={occ.first_name} onChange={(e) => onChange("first_name", e.target.value)} className="h-10 text-sm" data-testid={`occ-first-${idx}`} />
          </div>
          <div className="flex-1 min-w-[140px]">
            <Label className="text-[11px] text-[#635F59]">Cognome *</Label>
            <Input value={occ.last_name} onChange={(e) => onChange("last_name", e.target.value)} className="h-10 text-sm" data-testid={`occ-last-${idx}`} />
          </div>
        </div>
        {/* Sesso + Età + Data di nascita */}
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-[11px] text-[#635F59]">Sesso *</Label>
            <div className="inline-flex gap-1 mt-1">
              <button
                type="button"
                onClick={() => onChange("gender", "M")}
                className={`w-12 h-10 rounded border text-sm font-semibold transition ${occ.gender === "M" ? "bg-[#AC5D45] text-white border-[#AC5D45]" : "bg-white border-[#E8E2D9] text-[#635F59]"}`}
                data-testid={`occ-gender-m-${idx}`}
              >M</button>
              <button
                type="button"
                onClick={() => onChange("gender", "F")}
                className={`w-12 h-10 rounded border text-sm font-semibold transition ${occ.gender === "F" ? "bg-[#AC5D45] text-white border-[#AC5D45]" : "bg-white border-[#E8E2D9] text-[#635F59]"}`}
                data-testid={`occ-gender-f-${idx}`}
              >F</button>
            </div>
          </div>
          <div className="w-16">
            <Label className="text-[11px] text-[#635F59]">Età</Label>
            <Input
              placeholder="00"
              type="number"
              inputMode="numeric"
              min="0"
              max="120"
              value={occ.age ?? ""}
              onChange={(e) => onChange("age", e.target.value.replace(/\D/g, "").slice(0, 3))}
              className="h-10 text-sm text-center"
              data-testid={`occ-age-${idx}`}
            />
          </div>
          <div className="flex-1 min-w-[220px]">
            <Label className="text-[11px] text-[#635F59]">Data nascita *</Label>
            <DobSelect
              value={occ.date_of_birth || ""}
              onChange={(v) => onChange("date_of_birth", v)}
              testIdPrefix={`occ-dob-${idx}`}
            />
          </div>
        </div>
        {/* Cittadinanza + Stato nascita — combobox con autocomplete */}
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[160px]">
            <Label className="text-[11px] text-[#635F59]">Cittadinanza *</Label>
            <CountryCombo
              value={occ.citizenship}
              onChange={(v) => onChange("citizenship", v)}
              testId={`occ-citi-${idx}`}
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <Label className="text-[11px] text-[#635F59]">Stato di nascita *</Label>
            <CountryCombo
              value={occ.birth_country}
              onChange={(v) => onChange("birth_country", v)}
              testId={`occ-bcountry-${idx}`}
            />
          </div>
        </div>
        {occ.birth_country?.toUpperCase() === "ITA" && (
          <div className="flex flex-wrap gap-2">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-[11px] text-[#635F59]">Comune di nascita *</Label>
              <ComuneCombo
                value={occ.birth_place}
                onChange={(v) => onChange("birth_place", v)}
                onProvinceAutofill={(sigla) => onChange("birth_province", sigla)}
                onIstatAutofill={(code) => onChange("birth_place_istat", code)}
                testId={`occ-bplace-${idx}`}
              />
            </div>
            <div className="w-16">
              <Label className="text-[11px] text-[#635F59]">Prov.</Label>
              <Input value={occ.birth_province} onChange={(e) => onChange("birth_province", e.target.value.toUpperCase().slice(0, 2))} className="h-10 text-sm uppercase text-center" maxLength={2} placeholder="RC" data-testid={`occ-bprov-${idx}`} />
            </div>
          </div>
        )}
        {/* Città di nascita per ospiti stranieri (iter 135) — sempre
            visibile quando lo Stato di nascita non è Italia. La Polizia
            Alloggiati accetta il nome libero per i comuni esteri. */}
        {occ.birth_country && occ.birth_country.toUpperCase() !== "ITA" && (
          <div>
            <Label className="text-[11px] text-[#635F59]">Città di nascita</Label>
            <Input
              value={occ.birth_place}
              onChange={(e) => onChange("birth_place", e.target.value)}
              className="h-10 text-sm"
              placeholder="es. Berlin · Paris · New York"
              data-testid={`occ-bplace-foreign-${idx}`}
            />
          </div>
        )}
        {/* Documento d'identità (iter 135) — opzionale, ma se compilato
            qui la app Emmanuel Hospitality lo trova già pronto all'arrivo
            e l'ospite non lo deve digitare di nuovo. Espandibile per
            tenere il form snello quando l'host vuole solo l'anagrafica
            base. */}
        <details className="bg-white border border-[#E8E2D9] rounded-lg p-2.5" data-testid={`occ-doc-${idx}`}>
          <summary className="cursor-pointer text-[12px] font-semibold text-[#1E3A5F] flex items-center justify-between">
            <span>📇 Documento d'identità (facoltativo)</span>
            {(occ.document_number || "").trim() && (
              <span className="text-[10px] text-emerald-700 font-medium">✓ pre-compilato</span>
            )}
          </summary>
          <p className="text-[10px] text-[#9C9890] mt-1.5 leading-snug">
            Inserendo questi dati qui, all'arrivo l'app <strong>Emmanuel Hospitality</strong> li troverà già pronti.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
            <div>
              <Label className="text-[10px] text-[#635F59]">Tipo</Label>
              <select
                value={occ.document_type || ""}
                onChange={(e) => onChange("document_type", e.target.value)}
                className="h-10 w-full px-2 text-sm bg-white border border-[#E8E2D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#AC5D45]"
                data-testid={`occ-doctype-${idx}`}
              >
                <option value="">—</option>
                <option value="id_card">Carta d'identità</option>
                <option value="passport">Passaporto</option>
                <option value="driver_license">Patente</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] text-[#635F59]">Numero</Label>
              <Input
                value={occ.document_number}
                onChange={(e) => onChange("document_number", e.target.value.toUpperCase())}
                className="h-10 text-sm uppercase font-mono"
                placeholder="es. CA12345XX"
                data-testid={`occ-docnum-${idx}`}
              />
            </div>
            <div>
              <Label className="text-[10px] text-[#635F59]">Luogo rilascio</Label>
              <Input
                value={occ.document_issue_place}
                onChange={(e) => onChange("document_issue_place", e.target.value)}
                className="h-10 text-sm"
                placeholder="es. Comune di Reggio Calabria"
                data-testid={`occ-docplace-${idx}`}
              />
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
